/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';

import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {AdnApiClientService} from '../common/controller/adn-api-client.service';
import {
  SignalAdminResourceApiBase,
  SignalAdminResourceListParams,
  SignalAdminResourceModel,
  SignalAdminResourcePage
} from '../common/controller/signal-admin-resource-api';

export interface SegmentModel extends SignalAdminResourceModel {
  [key: string]: any;
}

@Injectable({providedIn: 'root'})
export class SegmentApiService extends SignalAdminResourceApiBase<SegmentModel> {
  constructor(
    client: AdnApiClientService,
    private readonly localUserProfile: LocalUserProfile,
    private readonly localNetworkProfile: LocalNetworkProfile
  ) {
    super(client, 'segments');
  }

  override list(params: SignalAdminResourceListParams): Observable<SignalAdminResourcePage<SegmentModel>> {
    return super.list({
      ...params,
      ...(this.localUserProfile.getCurrentApp() === 'ADVERTISING' ? {} : {dataSource: 'ADNUNTIUS'})
    });
  }

  searchForTargeting(params: Record<string, any>): Observable<SignalAdminResourcePage<SegmentModel>> {
    return this.client.request({
      method: 'GET',
      url: this.resourceUri('segments'),
      params: {
        includeHidden: false,
        includeActive: true,
        includeInactive: false,
        orderBy: 'name',
        ...params
      }
    });
  }

  postTargeting(targeting: any, params: Record<string, any>): Observable<SignalAdminResourcePage<SegmentModel>> {
    return this.client.request({
      method: 'POST',
      url: this.resourceUri('segments/withtargeting'),
      params: {format: 'json', concise: true, ...params},
      body: targeting
    });
  }

  getByIds(segmentIds: string[]): Observable<SignalAdminResourcePage<SegmentModel>> {
    if (!segmentIds.length) {
      return of({results: [], totalCount: 0});
    }
    return this.client.request({
      method: 'GET',
      url: this.resourceUri('segments'),
      params: {
        context: this.localNetworkProfile.getNetworkId(),
        id: segmentIds.join(';'),
        pageSize: segmentIds.length
      }
    });
  }
}
